// Builds a relative URL that hits /api/click for click-tracking +
// affiliate-id-injection on outbound booking links. The server logs
// the click to Redis (per-trip list) and 302s to the target with any
// configured affiliate ID appended for matching domains.
//
// Used for: booking URLs on accommodation items. Source URLs and Maps
// links currently stay direct; we route through here only when there's
// a monetization story.

export type ClickKind = "booking" | "source" | "video" | "external";

interface BuildClickUrlOpts {
  url: string;
  tripId?: string;
  itemId?: string;
  kind?: ClickKind;
}

export function buildClickUrl({
  url,
  tripId,
  itemId,
  kind = "external",
}: BuildClickUrlOpts): string {
  if (!url) return url;
  const params = new URLSearchParams();
  params.set("url", url);
  if (tripId) params.set("trip", tripId);
  if (itemId) params.set("item", itemId);
  params.set("kind", kind);
  return `/api/click?${params.toString()}`;
}

// Constructs a Booking.com searchresults URL for the trip's destination
// with the check-in / check-out dates pre-filled. Predicting Booking's
// per-hotel URLs needs their internal hotel IDs, which a name alone does
// not give, so a search is the only stable deep link.
//
// It searches the CITY, not the hotel name. Booking reads ss, ssne and
// ssne_untouched as one destination string that must resolve, and all
// three must be identical: a mismatch bounces to the Booking homepage,
// and a string it cannot resolve renders an EMPTY location box with zero
// results, which is what "the location did not carry over" looked like.
// Hotel names resolve only sometimes. Verified live: "Hotel Okura Kyoto"
// and "The Ritz-Carlton Kyoto" resolve; "Nine Hours Woman Kyoto" (a real
// property), "Stay at the Ritz-Carlton Kyoto" and any descriptive title
// do not, while "Kyoto" always does. So the city is what we send: the
// location and the dates always carry over, and the page always lists
// real rooms at real prices. The exact hotel is named on the card, with
// its own Google Maps link to the listing.
export function buildBookingSearchUrl(opts: {
  hotelName: string;
  destination?: string | null;
  checkIn?: string | null;
  checkOut?: string | null;
}): string {
  const name = opts.hotelName?.trim() || "";
  const city = opts.destination?.trim() || "";
  // The hotel name is the fallback only when there is no destination at
  // all, where a half-working search still beats no link.
  const query = city || name;
  const params = new URLSearchParams();
  if (query) {
    params.set("ss", query);
    params.set("ssne", query);
    params.set("ssne_untouched", query);
  }
  if (opts.checkIn) params.set("checkin", opts.checkIn);
  if (opts.checkOut) params.set("checkout", opts.checkOut);
  params.set("group_adults", "2");
  return `https://www.booking.com/searchresults.html?${params.toString()}`;
}
